import { Box, BoxProps } from '@chakra-ui/react'
import { forwardRef, ReactNode } from 'react'

import SectionTitle from './SectionTitle'

/**
 * Aparência única do card do painel: fundo, borda, cor da borda e raio.
 *
 * Eram cinco famílias do mesmo card, escritas por gente diferente em momentos diferentes — duas
 * sintaxes de borda (`border="1px"` e `border="1px solid"`), duas cores (`gray.100` e
 * `gray.200`) e cinco raios (`xl`, `lg`, `14px`, `16px`, `md`). O `EventTransactionsPage` e o
 * `SidebarBannerPage`, no mesmo módulo, desenhavam o mesmo card de dois jeitos.
 *
 * O escolhido é o da família maior (Events/Contests, 25 dos 47 cards) — e é também o único que
 * já estava em componente compartilhado, no `RevenueSummaryCards`.
 *
 * É exportado porque o `StatCard` monta a mesma casca sobre um `<Stat>`.
 */
export const SECTION_CARD_SHELL: BoxProps = {
  bg: 'white',
  borderRadius: 'xl',
  border: '1px',
  borderColor: 'gray.100'
}

export type SectionCardVariant = 'section' | 'tile' | 'flush'

/**
 * Token para o formato do conteúdo, não medida do Chakra — a mesma ideia do `size` do
 * `FormModal` e do `LoadingBlock`.
 *
 * `section` é o padrão: um bloco da página, com o respiro que 25 dos 47 cards já tinham.
 * `tile` é o card pequeno dentro de uma grade (o curso vinculado, o indicador financeiro de
 * 200px), onde `p={6}` come o espaço do conteúdo. `flush` é o card cujo conteúdo encosta na
 * borda — uma tabela, uma imagem, um iframe — e por isso não tem padding nenhum; o `overflow`
 * é o que faz o conteúdo respeitar o raio do card.
 *
 * Padding era `p` 2/3/4/5/6/8 mais um `px={6} py={5}`. Nenhum desses números era decisão: o
 * `EventAdminPage` usa `p={5}` e o `EventDetailPage` `p={6}` para cards irmãos na mesma tela.
 */
const SECTION_CARD_VARIANTS: Record<SectionCardVariant, BoxProps> = {
  section: { p: 6 },
  tile: { p: 4 },
  flush: { overflow: 'hidden' }
}

export interface ISectionCardProps extends Omit<BoxProps, 'title' | 'variant'> {
  /** Título da seção. Renderiza um `<SectionTitle>` — não monte um `<Heading>` dentro do card. */
  title?: ReactNode
  /** Ações à direita do título: um botão, um selo, um contador. */
  actions?: ReactNode
  /** Formato do conteúdo. `section` (padrão), `tile` para card de grade, `flush` para tabela/imagem. */
  variant?: SectionCardVariant
  /** Nível do heading do título, quando o card está dentro de outra seção já titulada. */
  titleAs?: 'h2' | 'h3' | 'h4'
}

/**
 * Card de seção do painel — o bloco branco que agrupa um pedaço de uma página.
 *
 * A página declara o título, as ações e o formato do conteúdo; borda, raio, cor e padding vêm
 * daqui. Eram 47 cards em 13 arquivos com 2 sintaxes de borda, 5 raios e 6 paddings, e **zero
 * uso do `<Card>` do Chakra** — que não serve aqui porque traz sombra e variantes próprias que
 * o painel não usa.
 *
 * Encaminha a `ref` de propósito: os quatro cards com animação de entrada do `ArtworkEvaluation`
 * e do `SurveyReportPage` continuam sendo `motion(SectionCard)`, em vez de ficarem de fora do
 * eixo desenhando a casca à mão.
 */
const SectionCard = forwardRef<HTMLDivElement, ISectionCardProps>(function SectionCard(
  { title, actions, variant = 'section', titleAs = 'h2', children, ...rest },
  ref
) {
  const header = title != null || actions != null

  return (
    <Box ref={ref} {...SECTION_CARD_SHELL} {...SECTION_CARD_VARIANTS[variant]} {...rest}>
      {header &&
        // Sem padding no corpo, o título não pode flutuar dentro dele: vira uma barra própria,
        // separada por uma linha. É o que o `ContactSnippetPage` já fazia nos seus dois cards
        // de conteúdo sangrado, e o motivo de `px`/`py` aqui baterem com o `p={6}` de `section`.
        (variant === 'flush' ? (
          <Box px={6} py={4} borderBottom="1px solid" borderColor="gray.100">
            <SectionTitle as={titleAs} actions={actions} mb={0}>
              {title}
            </SectionTitle>
          </Box>
        ) : (
          <SectionTitle as={titleAs} actions={actions}>
            {title}
          </SectionTitle>
        ))}
      {children}
    </Box>
  )
})

export default SectionCard
